import React from 'react';
import { observer } from 'mobx-react';

import { CircularProgress } from '@mui/material';

import { formatMoment } from 'utils/formatters/timezone';

import { DEFAULT_TIME_FORMAT } from 'core/constants';

import { Button } from 'components/Button';

import CustomModal from 'components/CustomModal';
import InitialAvatar from 'components/Avatar/InitialAvatar';

import HistoryRow from 'components/Entities/EntityHistoryTab/HistoryRow';
import type { EntityHistoryResponseType } from 'utils/api/modules/entities/entities.types';

import { MembershipMode, MemberGroupHistoryRow } from 'utils/api/modules/memberGroups';

import RuleSummary from 'containers/MemberGroups/components/RuleBuilder/RuleSummary';

import {
  HistoryBatch,
  HistoryBatchDot,
  HistoryBatchLine,
  HistoryBatchLoading,
  HistoryBatchModalList,
  HistoryBatchName,
  HistoryBatchPartial,
  HistoryBatchRow,
  HistoryBatchTime,
  HistoryBatchToggle,
  InlineRule,
} from './styles';
import { batchSizeOf, isBatched } from './history';
import { useBatchMembers } from './useBatchMembers';

type HistoryEntryRowProps = { entry: MemberGroupHistoryRow; groupUuid?: string };

const memberName = (entry: MemberGroupHistoryRow): React.ReactNode => <b>{entry.user?.name ?? 'Someone'}</b>;

const actorSuffix = (entry: MemberGroupHistoryRow): React.ReactNode => {
  if (entry.cause === 'matched_rule' || entry.cause === 'rule_updated') {
    return 'automatically';
  }
  if (entry.cause === 'deactivation') {
    return 'by the system';
  }
  return entry.actor ? (
    <>
      by <b>{entry.actor.name}</b>
    </>
  ) : (
    'by the system'
  );
};

const changeLine = (entry: MemberGroupHistoryRow): React.ReactNode => {
  const isAutomatic = entry.cause === 'matched_rule' || entry.cause === 'rule_updated';

  switch (entry.eventType) {
    case 'group_created':
      return 'Group created';
    case 'renamed':
      return entry.detail ? (
        <>
          Renamed to “<b>{entry.detail}</b>”
        </>
      ) : (
        'Group renamed'
      );
    case 'description_changed':
      return 'Description updated';
    case 'rule_changed':
      return entry.rule ? (
        <>
          Membership rule updated ·{' '}
          <InlineRule>
            <RuleSummary rule={entry.rule} />
          </InlineRule>
        </>
      ) : (
        'Membership rule updated'
      );
    case 'mode_switched':
      return entry.toMode === MembershipMode.Dynamic ? 'Switched to Dynamic' : 'Switched to Manual';
    case 'member_added':
      return isAutomatic ? <>{memberName(entry)} joined the group</> : <>{memberName(entry)} added to the group</>;
    case 'member_removed':
      if (entry.cause === 'deactivation') {
        return <>{memberName(entry)} removed after being deactivated</>;
      }
      return isAutomatic ? <>{memberName(entry)} left the group</> : <>{memberName(entry)} removed from the group</>;
    case 'group_activated':
      return 'Group reactivated';
    case 'group_deactivated':
      return 'Group deactivated';
    default:
      return 'Group updated';
  }
};

export const historyContent = (entry: MemberGroupHistoryRow): React.ReactNode => (
  <>
    {changeLine(entry)} {actorSuffix(entry)}
  </>
);

const asHistoryRowItem = (entry: MemberGroupHistoryRow): EntityHistoryResponseType =>
  ({
    entityField: { key: 'group', uuid: entry.uuid },
    createdAt: entry.createdAt,
    current: null,
    previous: null,
    createdBy: null,
  }) as unknown as EntityHistoryResponseType;

// The count leads (it carries the change), then the same clause a single row uses.
const batchHeadline = (entry: MemberGroupHistoryRow): string => {
  const count = batchSizeOf(entry);
  const noun = count === 1 ? 'member' : 'members';
  if (entry.cause === 'deactivation') {
    return `${count} ${noun} removed after being deactivated`;
  }
  if (entry.cause === 'manual') {
    return entry.eventType === 'member_added'
      ? `${count} ${noun} added to the group`
      : `${count} ${noun} removed from the group`;
  }
  return entry.eventType === 'member_added' ? `${count} ${noun} joined` : `${count} ${noun} left`;
};

const BatchHistoryRow: React.FunctionComponent<HistoryEntryRowProps> = ({ entry, groupUuid }) => {
  const [listOpen, setListOpen] = React.useState(false);
  const { names, hasMore, needsButton, loading, failed, loadMore, onScroll, listRef } = useBatchMembers(
    groupUuid,
    entry
  );

  return (
    <HistoryBatchRow data-testid="group-history-entry">
      <HistoryBatchTime data-testid="history-entry-time">
        {formatMoment(entry.createdAt, DEFAULT_TIME_FORMAT)}
      </HistoryBatchTime>
      <HistoryBatchDot />
      <HistoryBatch>
        <HistoryBatchLine>
          <b>{batchHeadline(entry)}</b> {actorSuffix(entry)}
        </HistoryBatchLine>
        <HistoryBatchToggle type="button" onClick={() => setListOpen(true)}>
          View members
        </HistoryBatchToggle>
      </HistoryBatch>
      <CustomModal
        openModal={listOpen}
        onClose={() => setListOpen(false)}
        modalTitle={batchHeadline(entry)}
        submitButton={
          <Button variant="contained" size="large" onClick={() => setListOpen(false)}>
            Close
          </Button>
        }
      >
        <HistoryBatchModalList ref={listRef} onScroll={onScroll} data-testid="group-history-batch-list">
          {names.map((name, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <HistoryBatchName key={`${name}-${index}`}>
              <InitialAvatar name={name} size="small" />
              {name}
            </HistoryBatchName>
          ))}
          {hasMore && (
            <HistoryBatchPartial data-testid="group-history-batch-partial">
              {failed ? (
                <>
                  <span data-testid="group-history-batch-error">We are unable to load more members.</span>
                  <Button
                    variant="outlined"
                    size="large"
                    data-testid="group-history-batch-retry"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    Try again
                  </Button>
                </>
              ) : (
                <HistoryBatchLoading>
                  {loading && <CircularProgress size={20} />}
                  Showing {names.length} of {batchSizeOf(entry)}
                  {needsButton && (
                    <Button
                      variant="outlined"
                      size="large"
                      data-testid="group-history-batch-load-more"
                      onClick={loadMore}
                    >
                      Load more
                    </Button>
                  )}
                </HistoryBatchLoading>
              )}
            </HistoryBatchPartial>
          )}
        </HistoryBatchModalList>
      </CustomModal>
    </HistoryBatchRow>
  );
};

const ObservedBatchHistoryRow = observer(BatchHistoryRow);

const HistoryEntryRow: React.FunctionComponent<HistoryEntryRowProps> = ({ entry, groupUuid }) =>
  isBatched(entry) ? (
    <ObservedBatchHistoryRow entry={entry} groupUuid={groupUuid} />
  ) : (
    <div data-testid="group-history-entry">
      <HistoryRow item={asHistoryRowItem(entry)}>{historyContent(entry)}</HistoryRow>
    </div>
  );

export default HistoryEntryRow;
